import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { FiSend, FiX } from "react-icons/fi";
import { Bio } from "../../data/constants";

// Docked "ask me anything" widget, replacing the centered ⌘K command palette.
// The RAG backend (api/ask.js, server.js) and its SSE contract are unchanged —
// only the shell is new.

const ASK_ENDPOINT = process.env.REACT_APP_AMA_ENDPOINT || "/api/ask";

const SUGGESTIONS = [
  "Who are you?",
  "What do you work on?",
  "Tell me about your RAG work",
];

/**
 * POSTs a question and streams the SSE response.
 * Calls onToken with the accumulated answer, onSources with citation payloads.
 * Resolves once the stream closes; throws only on transport failure.
 */
async function streamAnswer(question, { onToken, onSources }) {
  const res = await fetch(ASK_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ question: question.slice(0, 500), history: [] }),
  });

  const ct = res.headers.get("content-type") || "";
  if (!res.ok || !res.body || !ct.includes("text/event-stream")) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || "Couldn't reach the assistant.");
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let acc = "";

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });

    let sep;
    while ((sep = buf.indexOf("\n\n")) >= 0) {
      const rawEvent = buf.slice(0, sep);
      buf = buf.slice(sep + 2);

      let ev = "message";
      let dataStr = "";
      for (const line of rawEvent.split("\n")) {
        if (line.startsWith("event:")) ev = line.slice(6).trim();
        else if (line.startsWith("data:")) dataStr += line.slice(5).trim();
      }
      if (!dataStr) continue;

      if (ev === "sources") {
        try {
          onSources(JSON.parse(dataStr));
        } catch {}
      } else if (ev === "token") {
        try {
          acc += JSON.parse(dataStr);
          onToken(acc);
        } catch {}
      }
    }
  }

  return acc;
}

const Fab = styled.button`
  position: fixed;
  right: 22px;
  bottom: 22px;
  z-index: 950;

  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;

  cursor: pointer;
  border-radius: 0;
  background: ${({ theme }) => theme.frameInk};
  border: 2px solid ${({ theme }) => theme.orange};
  box-shadow: ${({ theme }) => theme.bevel} rgba(0, 0, 0, 0.55);
  color: ${({ theme }) => theme.orange};

  &:hover {
    background: ${({ theme }) => theme.orange};
    color: ${({ theme }) => theme.frameInk};
  }

  @media (max-width: 560px) {
    right: 14px;
    bottom: 14px;
  }
`;

const Panel = styled(motion.div)`
  position: fixed;
  right: 22px;
  bottom: 22px;
  z-index: 960;

  width: 380px;
  max-width: calc(100vw - 28px);
  height: 520px;
  max-height: calc(100vh - 44px);

  display: flex;
  flex-direction: column;
  overflow: hidden;

  background: ${({ theme }) => theme.biome.about.card};
  border: 1px solid ${({ theme }) => theme.orange};
  border-radius: 0;
  box-shadow: ${({ theme }) => theme.bevel} rgba(0, 0, 0, 0.6);

  @media (max-width: 560px) {
    right: 14px;
    bottom: 14px;
  }
`;

// Same title-bar chrome as the section windows.
const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: ${({ theme }) => theme.frameInk};
  border-bottom: 1px solid rgba(255, 255, 255, 0.16);
`;

const Avatar = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex: none;
  background: ${({ theme }) => theme.orange};
  color: ${({ theme }) => theme.frameInk};
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: 14px;
  font-weight: 900;
`;

const BarText = styled.div`
  flex: 1;
  min-width: 0;
`;

const BarName = styled.div`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: 13px;
  font-weight: 900;
  color: #ffffff;
`;

const BarSub = styled.div`
  font-family: ${({ theme }) => theme.fonts.ui};
  font-size: 10px;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.biome.about.muted};
`;

const IconBtn = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  cursor: pointer;
  border-radius: 0;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.24);
  color: #ffffff;

  &:hover {
    background: ${({ theme }) => theme.orange};
    border-color: ${({ theme }) => theme.orange};
    color: ${({ theme }) => theme.frameInk};
  }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 18px 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Greeting = styled.div`
  text-align: center;
  margin-top: 12px;
`;

const GreetTitle = styled.div`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: 17px;
  font-weight: 900;
  color: ${({ theme }) => theme.biome.about.ink};
  margin-bottom: 5px;
`;

const GreetSub = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.biome.about.muted};
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 7px;
  margin-top: 18px;
`;

const SuggestChip = styled.button`
  cursor: pointer;
  padding: 8px 11px;
  border-radius: 0;
  background: rgba(255, 130, 54, 0.12);
  border: 1px solid ${({ theme }) => theme.orange};
  color: ${({ theme }) => theme.orange};

  font-family: ${({ theme }) => theme.fonts.ui};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;

  &:hover {
    background: ${({ theme }) => theme.orange};
    color: ${({ theme }) => theme.frameInk};
  }
`;

const Bubble = styled.div`
  align-self: ${({ $me }) => ($me ? "flex-end" : "flex-start")};
  max-width: 88%;
  padding: 10px 12px;
  border-radius: 0;

  font-size: 13.5px;
  line-height: 1.5;
  white-space: pre-wrap;

  background: ${({ $me, theme }) =>
    $me ? theme.orange : "rgba(255, 255, 255, 0.06)"};
  color: ${({ $me, theme }) =>
    $me ? theme.frameInk : theme.biome.about.ink};
  border: 1px solid
    ${({ $me, theme }) => ($me ? theme.orange : "rgba(255,255,255,0.16)")};
`;

const Status = styled.div`
  align-self: flex-start;
  font-family: ${({ theme }) => theme.fonts.ui};
  font-size: 11px;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  color: ${({ $error, theme }) =>
    $error ? "#e06a5a" : theme.biome.about.muted};
`;

const InputRow = styled.form`
  display: flex;
  gap: 7px;
  padding: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.16);
`;

const Input = styled.input`
  flex: 1;
  min-width: 0;
  padding: 9px 10px;
  border-radius: 0;
  background: ${({ theme }) => theme.biome.about.bg};
  border: 1px solid rgba(255, 255, 255, 0.24);
  color: ${({ theme }) => theme.biome.about.ink};
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: 13.5px;

  &::placeholder {
    color: ${({ theme }) => theme.biome.about.muted};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.orange};
  }
`;

// A quill nib, matching the objective's FAB glyph. Flat polygons, hard edges.
const Quill = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M20 3 C12 4 7 9 6 16 L4 21 L9 19 C16 18 21 12 20 3 Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="miter"
    />
    <path d="M15 8 L7 18" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const AskWidget = () => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [turns, setTurns] = useState([]); // { me: bool, text }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const bodyRef = useRef(null);
  const inputRef = useRef(null);

  // Keep the newest turn in view as tokens stream in.
  useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns, busy]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 40);
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const ask = async (question) => {
    const text = question.trim();
    if (!text || busy) return;

    setQ("");
    setError("");
    setBusy(true);
    setTurns((t) => [...t, { me: true, text }, { me: false, text: "" }]);

    // Replace the trailing placeholder turn as tokens arrive.
    const write = (answer) =>
      setTurns((t) => {
        const next = [...t];
        next[next.length - 1] = { me: false, text: answer };
        return next;
      });

    try {
      const acc = await streamAnswer(text, {
        onToken: write,
        onSources: () => {},
      });
      if (!acc) {
        write("");
        setError("No answer came back. Try rephrasing.");
      }
    } catch (e) {
      write("");
      setError(
        "Couldn't reach the assistant. The AMA server may not be running (npm run ama)."
      );
    } finally {
      setBusy(false);
    }
  };

  const initial = (Bio.name || "?").charAt(0);
  const firstName = (Bio.name || "").split(" ")[0];

  return (
    <>
      {!open && (
        <Fab onClick={() => setOpen(true)} aria-label="Ask me anything">
          <Quill />
        </Fab>
      )}

      <AnimatePresence>
        {open && (
          <Panel
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.18, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <Bar>
              <Avatar>{initial}</Avatar>
              <BarText>
                <BarName>{Bio.name}</BarName>
                <BarSub>Ask me anything</BarSub>
              </BarText>
              <IconBtn onClick={() => setOpen(false)} aria-label="Close">
                <FiX />
              </IconBtn>
            </Bar>

            <Body ref={bodyRef}>
              {turns.length === 0 && (
                <Greeting>
                  <GreetTitle>Hey, I'm {firstName}.</GreetTitle>
                  <GreetSub>
                    Ask me about my work, projects, or anything.
                  </GreetSub>
                  <Chips>
                    {SUGGESTIONS.map((s) => (
                      <SuggestChip key={s} onClick={() => ask(s)}>
                        {s}
                      </SuggestChip>
                    ))}
                  </Chips>
                </Greeting>
              )}

              {turns.map(
                (t, i) =>
                  t.text && (
                    <Bubble key={i} $me={t.me}>
                      {t.text}
                    </Bubble>
                  )
              )}

              {busy && <Status>Thinking…</Status>}
              {error && <Status $error>{error}</Status>}
            </Body>

            <InputRow
              onSubmit={(e) => {
                e.preventDefault();
                ask(q);
              }}
            >
              <Input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Ask me anything..."
                aria-label="Ask me anything"
              />
              <IconBtn as="button" type="submit" aria-label="Send">
                <FiSend />
              </IconBtn>
            </InputRow>
          </Panel>
        )}
      </AnimatePresence>
    </>
  );
};

export default AskWidget;
